import JSConfetti from 'js-confetti'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'

import { useOnboardingStep } from 'components/hooks/employer_onboarding/useOnboardingStep'
import { SkeletonItem } from 'components/reusable/SkeletonItem'
import { Banner, Button, usePageTitle } from 'slab'
import {
  useGetEmployersOnboardingCompleteOptions as useCompleteOnboardingOptions,
  usePostEmployersOnboardingComplete as useCompleteOnboardingMutation,
} from 'utils/web_api'
import { useWindowLocation } from 'utils/windowLocation'

import { CenteredLayout } from './CenteredLayout'
import { parseError } from './utils'

function useNonFlashingLoadingState (loading: boolean) {
  const [enoughTimeHasPassed, setEnoughTimeHasPassed] = useState(!loading)

  useEffect(() => {
    const timeout = setTimeout(() => {
      setEnoughTimeHasPassed(true)
    }, 1000)

    return () => clearTimeout(timeout)
  }, [])

  return !enoughTimeHasPassed || loading
}

export function CompletePage () {
  usePageTitle('Account setup complete')

  const { assign } = useWindowLocation()
  const { data, error, isLoading } = useCompleteOnboardingOptions()
  const completeOnboardingMutation = useCompleteOnboardingMutation()
  const loading = useNonFlashingLoadingState(isLoading)
  const { navigateToPreviousStep } = useOnboardingStep()

  useEffect(() => {
    if (!loading && !error) {
      const confetti = new JSConfetti()
      confetti.addConfetti({
        confettiColors: [
          '#9C3BFF', // thatch-500
          '#FFC879', // orange-300
          '#0094D5', // blue-400
          '#8051FF', // violet-400
          '#EC3939', // red-400
          '#FFE655', // yellow-300
          '#47CA67', // green-300
        ],
      })

      return () => {
        confetti.destroyCanvas()
      }
    }
  }, [loading, error])

  if (loading || error) {
    return (
      <CenteredLayout key='loading' displaySupportLink>
        <CenteredLayout.Header
          heading='We’re finishing up your onboarding...'
          subheading={
            !error && (
              <div className='flex flex-col items-center'>
                <div className='max-w-48 w-full'>
                  <SkeletonItem />
                </div>
                <div className='max-w-60 w-full'>
                  <SkeletonItem />
                </div>
                <div className='max-w-36 w-full'>
                  <SkeletonItem />
                </div>
              </div>
            )
          }
          subheadingAsChild
        />
        {!!error && (
          <Banner type='critical' title='An error occurred loading this page'>
            {parseError(error, '').message}
          </Banner>
        )}
        <CenteredLayout.Buttons>
          <Button
            variant='secondary'
            onClick={navigateToPreviousStep}
          >
            Back
          </Button>
          <Button variant='primary' disabled>
            Go to dashboard
          </Button>
        </CenteredLayout.Buttons>
      </CenteredLayout>
    )
  }

  return (
    <CenteredLayout key='complete' displaySupportLink>
      <CenteredLayout.Header
        heading='You’re all set!'
        subheading={
          data!.can_invite_employees
            ? "Now that you've finished onboarding, you can invite your team to Thatch. When you invite your employees, they'll receive an email with instructions to create an account and select their health benefits."
            : "We'll reach out to confirm your allowances and invite your employees."
        }
      />
      <CenteredLayout.Buttons>
        <Button
          variant='secondary'
          onClick={navigateToPreviousStep}
        >
          Back
        </Button>
        <Button
          variant='primary'
          onClick={() => {
            completeOnboardingMutation.mutate(undefined, {
              onSuccess: () => {
                assign(data!.dashboard_url ?? '')
              },
              onError: () => {
                toast.error(
                  'Could not direct you to the dashboard because of an error.'
                )
              },
            })
          }}
        >
          Go to dashboard
        </Button>
      </CenteredLayout.Buttons>
    </CenteredLayout>
  )
}
